<template>
  <ts-offcanvas id="notebook-sidebar" panel-class="ts-notebook" labelledby="notebook-sidebar-title">
    <template #default="{ close }">
      <div class="offcanvas-header ts-panel-header">
        <div class="d-flex align-items-center gap-3">
          <div class="ts-panel-title-icon d-flex align-items-center justify-content-center">
            <i class="bi bi-journal-bookmark" aria-hidden="true"></i>
          </div>
          <h2 id="notebook-sidebar-title" class="offcanvas-title ts-panel-title fw-bold">{{ translations[10966] }}</h2>
        </div>
        <button class="btn-close" type="button" :aria-label="closeLabel" @click="close"></button>
      </div>

      <div class="offcanvas-body ts-panel-body">
        <div class="accordion accordion-flush ts-panel-accordion ts-notebook-accordion">
          <div v-for="section in sections" :key="section.key" class="accordion-item">
            <h3 class="accordion-header">
              <button
                class="accordion-button"
                :class="{ collapsed: openSection !== section.key }"
                type="button"
                :aria-expanded="String(openSection === section.key)"
                :aria-controls="`notebook-${section.key}`"
                @click="openSection = openSection === section.key ? '' : section.key"
              >
                <span class="d-inline-flex align-items-center gap-2">
                  {{ section.title }}
                  <span class="badge rounded-pill ts-count-badge">{{ section.items.length }}</span>
                </span>
              </button>
            </h3>
            <div v-show="openSection === section.key" :id="`notebook-${section.key}`" class="accordion-collapse">
              <div class="accordion-body">
                <notebook-item
                  v-for="item in section.items"
                  :key="item.id"
                  :id="item.id"
                  :title="item.title"
                  :description="item.description"
                  :date="item.date"
                  @open="item.open"
                  @delete="item.remove"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="ts-panel-footer ts-notebook-footer d-flex justify-content-end">
        <button class="btn btn-outline-danger ts-btn-sm" type="button" :title="translations[13794]" @click="confirmDeleteAll">
          <i class="bi bi-trash me-2" aria-hidden="true"></i>
          {{ translations[13793] }}
        </button>
      </div>
    </template>
  </ts-offcanvas>
</template>

<script lang="ts">
import { AppModule, SettingsCounters } from '@/store/modules/app';
import { Component, Vue } from 'vue-property-decorator';
import NotebookItem from '@/components/Notebook/NotebookItem.vue';
import { NotebookModule } from '@/store/modules/notebook';
import { UserModule } from '@/store/modules/user';
import { WorkersModule } from '@/store/modules/workers';
import { ProfessionModule } from '@/store/modules/profession';
import { CompanyModule } from '@/store/modules/company';
import { groupCurses } from '@/config/configuration';
import { openCourse } from '@/sections/courses/search';
import TsOffcanvas from '@/components/Talentspace/layout/TsOffcanvas.vue';

interface NotebookEntry {
  id: number
  title: string
  description?: string
  date: string
  open: () => void
  remove: () => void
}

interface NotebookSection {
  key: string
  title: string
  items: NotebookEntry[]
}

@Component({
  components: {
    NotebookItem,
    TsOffcanvas,
  },
})
export default class NotebookSidebar extends Vue {
  // All sections start closed, as in the design
  openSection = '';

  get closeLabel() {
    return AppModule.translation[2511];
  }

  // Sections shown per portal (counters in PAT); items open the matching detail panel
  get sections(): NotebookSection[] {
    const sections: (NotebookSection & { visible: boolean })[] = [
      {
        key: 'jobs',
        visible: Boolean(this.isNotebookJobsHidden),
        title: this.translations[12604],
        items: this.notebookJobs.map((item: any) => ({
          id: item.id,
          title: item.position,
          description: item.location,
          date: this.formatDateEuropean(item.lastdate),
          open: () => this.openPanel('jobs-sidebar', () => this.handleJobSidebar(item.id, item.company_name, item.shortview)),
          remove: () => this.deleteNotepadJob(item.id),
        })),
      },
      {
        key: 'workers',
        visible: Boolean(this.isNotebookWorkersHidden),
        title: this.isCourses ? this.translations[15399] : this.translations[4741],
        items: this.notebookWorkers.map((item: any) => ({
          id: item.id,
          title: item.position,
          description: item.locations,
          date: this.formatDateEuropean(item.last_modified),
          open: () => this.openPanel('applicant-sidebar', () => this.handleWorkerSidebar(item.id)),
          remove: () => this.deleteNotepadWorker(item.id),
        })),
      },
      {
        key: 'companies',
        visible: Boolean(this.isNotebookCompanyHidden),
        title: this.translations[341],
        items: this.notebookCompany.map((item: any) => ({
          id: item.company_id,
          title: item.name,
          date: this.formatDateEuropean(item.last_modified),
          open: () => this.openPanel('company-sidebar', () => this.handleCompanySidebar(item.company_id, item.name)),
          remove: () => this.deleteNotepadCompany(item.company_id),
        })),
      },
      {
        key: 'courses',
        visible: Boolean(this.isNotebookCoursesHidden),
        title: this.translations[15404],
        items: this.notebookCourses.map((item: any) => ({
          id: item.id,
          title: item.course_name,
          description: item.location,
          date: this.formatDateEuropean(item.lastdate),
          open: () => this.handleCoursesSidebar(item.id),
          remove: () => this.deleteNotepadCourses(item.id),
        })),
      },
    ];
    return sections.filter((section) => section.visible);
  }

  // Detail panels are BootstrapVue sidebars opened by id
  openPanel(sidebarId: string, prepare: () => void) {
    prepare();
    this.$root.$emit('bv::toggle::collapse', sidebarId);
  }

  async confirmDeleteAll() {
    const confirmed = await this.$bvModal.msgBoxConfirm(this.translations[13794], {
      okTitle: this.translations[13793],
      okVariant: 'danger',
      cancelTitle: AppModule.translation[570],
      cancelVariant: 'outline-secondary',
      centered: true,
    });
    if (confirmed) await this.deleteAllNotice();
  }

  get isAuth() {
    return UserModule.isUserAuth;
  }

  get translations() {
    return AppModule.translation;
  }

  get notebookJobs() {
    return NotebookModule.notebookJobs;
  }

  get sid() {
    return AppModule.sid;
  }

  get isCourses() {
    return groupCurses(this.sid);
  }

  get getCounters() {
    return AppModule.settingPortal.counters;
  }

  isShowPage(value: SettingsCounters['name']) {
    return this.getCounters.find((val) => val.name === value)?.visible;
  }

  get isNotebookJobsHidden() {
    return this.isShowPage('jobs');
  }

  get isNotebookWorkersHidden() {
    return this.isShowPage('applicants');
  }

  get isNotebookCompanyHidden() {
    return this.isShowPage('companies');
  }

  get isNotebookCoursesHidden() {
    return this.isShowPage('courses');
  }

  get notebookWorkers() {
    return NotebookModule.notebookWorkers;
  }

  get notebookCompany() {
    return NotebookModule.notebookCompany;
  }

  get notebookCourses() {
    return NotebookModule.notebookCourses;
  }

  async deleteNotepadJob(id: number) {
    if (this.isAuth) {
      await NotebookModule.toogleNotepadItem({ type: 'job', item_id: id, isMarked: true });
    } else {
      NotebookModule.removeJobNotepadItemLocal({ id });
    }
  }

  async deleteNotepadCompany(id: number) {
    if (this.isAuth) {
      await NotebookModule.toogleNotepadItem({ type: 'company', item_id: id, isMarked: true });
    }
  }

  async deleteNotepadWorker(id: number) {
    if (this.isAuth) {
      await NotebookModule.toogleNotepadItem({ type: 'applicant', item_id: id, isMarked: true });
    } else {
      NotebookModule.removeWorkerNotepadItemLocal({ id });
    }
  }

  async deleteNotepadCourses(id: number) {
    if (this.isAuth) {
      await NotebookModule.toogleNotepadItem({ type: 'courses', item_id: id, isMarked: true });
    } else {
      NotebookModule.removeCourseNotepadItemLocal({ id });
    }
  }

  async deleteAllNotice() {
    let userType: 'applicant' | 'company' | 'consultant' | 'courses' | null = null;

    if (UserModule.accessTokenApplicant) {
      userType = 'applicant';
    } else if (UserModule.accessTokenCompany) {
      userType = 'company';
    } else if (UserModule.accessTokenConsultant) {
      userType = 'consultant';
    } else {
      userType = 'courses';
    }

    if (userType) {
      await NotebookModule.DeleteAllNotebookNotes(userType);
    }
  }

  formatDateEuropean(targetDate: string): string {
    if (targetDate) {
      const parts = targetDate.split('-');
      if (parts[2] && parts[1]) {
        return `${parts[2]}.${parts[1]}.${parts[0]}`;
      }
      return `${parts[0]}`;
    }
    return '';
  }

  handleWorkerSidebar(id: number) {
    WorkersModule.SET_SELECTED_WORKER(id);
  }

  handleJobSidebar(id: number, firmName: string, shortview: 1 | 0) {
    ProfessionModule.SetJobId(id);
    ProfessionModule.SetJobFirm(firmName);
    ProfessionModule.SetJobShort(shortview ? 'shortview' : '');
    CompanyModule.SetCompanyDetailFilter({ obj: 'idjob', value: id });
  }

  handleCompanySidebar(id: number, title: string) {
    CompanyModule.HandleCompanySidebar({ id, title });
  }

  // The list it came from when the course is in it (for ‹ ›), else the notebook entry alone
  handleCoursesSidebar(id: number) {
    const item = this.notebookCourses.find((el: any) => el.id === id);
    const list = ProfessionModule.courses?.courses || [];
    const course = list.find((el: any) => el.id === id);
    if (course) openCourse(this.$root, course, list);
    else if (item) openCourse(this.$root, item);
  }
}
</script>
<style scoped lang="scss">
.icon-event {
  transition: all .2s ease-in;
}

.accordion-button {
  color: #4d6069!important;
  &.not-collapsed {
    .icon-event {
      transform: rotate(90deg);
    }
  }
}
</style>
